// Maps the brands the Stylist tone names into search URLs on each brand's own
// site. The Skimlinks script (loaded in index.html) auto-rewrites these
// merchant-domain links into affiliate links when a user clicks — so the
// important thing is that the href points at a real merchant domain Skimlinks
// has a relationship with. The exact search query param matters only for UX.
//
// The explicit map below covers the brands the model names most often, so
// those clicks go straight to the brand's own site (best UX). But the model
// can name ANY brand contextually (athletic brands for Sport, suiting for
// Formal, etc.), so anything not in the map falls back to a Nordstrom search
// — a broad multi-brand Skimlinks merchant — which guarantees every example
// becomes a monetizable link.
//
// `match` is the lowercased, punctuation-stripped brand prefix we test the
// example string against. `search(q)` builds the destination URL.
// BRANDS_BY_LEN sorts longest-first so multi-word brands ("amazon
// essentials", "the north face") win over their shorter prefixes.

const BRANDS = [
  // ── Everyday / contemporary ──────────────────────────────────────────────
  { match: "amazon essentials",   search: (q) => `https://www.amazon.com/s?k=${q}+amazon+essentials` },
  { match: "old navy",            search: (q) => `https://oldnavy.gap.com/browse/search.do?searchText=${q}` },
  { match: "banana republic",     search: (q) => `https://bananarepublic.gap.com/browse/search.do?searchText=${q}` },
  { match: "buck mason",          search: (q) => `https://www.buckmason.com/search?q=${q}` },
  { match: "j.crew",              search: (q) => `https://www.jcrew.com/search?q=${q}` },
  { match: "jcrew",               search: (q) => `https://www.jcrew.com/search?q=${q}` },
  { match: "uniqlo",              search: (q) => `https://www.uniqlo.com/us/en/search?q=${q}` },
  { match: "everlane",            search: (q) => `https://www.everlane.com/search?q=${q}` },
  { match: "madewell",            search: (q) => `https://www.madewell.com/search?q=${q}` },
  { match: "abercrombie",         search: (q) => `https://www.abercrombie.com/shop/us/search?searchTerm=${q}` },
  { match: "bonobos",             search: (q) => `https://bonobos.com/search?q=${q}` },
  { match: "reformation",         search: (q) => `https://www.thereformation.com/search?q=${q}` },
  { match: "aritzia",             search: (q) => `https://www.aritzia.com/us/en/search?q=${q}` },
  { match: "cos",                 search: (q) => `https://www.cos.com/en_usd/search.html?q=${q}` },
  { match: "h&m",                 search: (q) => `https://www2.hm.com/en_us/search-results.html?q=${q}` },
  { match: "target",             search: (q) => `https://www.target.com/s?searchTerm=${q}` },
  { match: "gap",                 search: (q) => `https://www.gap.com/browse/search.do?searchText=${q}` },
  { match: "theory",              search: (q) => `https://www.theory.com/search?q=${q}` },
  { match: "vince",               search: (q) => `https://www.vince.com/search?q=${q}` },
  { match: "faherty",             search: (q) => `https://fahertybrand.com/search?q=${q}` },
  { match: "allsaints",           search: (q) => `https://www.us.allsaints.com/search?q=${q}` },
  { match: "levi's",              search: (q) => `https://www.levi.com/US/en_US/search/${q}` },
  { match: "levis",               search: (q) => `https://www.levi.com/US/en_US/search/${q}` },
  { match: "ralph lauren",        search: (q) => `https://www.ralphlauren.com/search?q=${q}` },
  { match: "a.p.c.",              search: (q) => `https://www.apc-us.com/search?q=${q}` },
  { match: "apc",                 search: (q) => `https://www.apc-us.com/search?q=${q}` },
  { match: "nordstrom",           search: (q) => `https://www.nordstrom.com/sr?keyword=${q}` },

  // ── Athletic / outdoor ───────────────────────────────────────────────────
  { match: "lululemon",           search: (q) => `https://shop.lululemon.com/search?Ntt=${q}` },
  { match: "nike",                search: (q) => `https://www.nike.com/w?q=${q}` },
  { match: "adidas",              search: (q) => `https://www.adidas.com/us/search?q=${q}` },
  { match: "new balance",         search: (q) => `https://www.newbalance.com/search?q=${q}` },
  { match: "hoka",                search: (q) => `https://www.hoka.com/en/us/search?q=${q}` },
  { match: "brooks",              search: (q) => `https://www.brooksrunning.com/en_us/search?q=${q}` },
  { match: "asics",               search: (q) => `https://www.asics.com/us/en-us/search/?q=${q}` },
  { match: "patagonia",           search: (q) => `https://www.patagonia.com/search/?q=${q}` },
  { match: "arc'teryx",           search: (q) => `https://arcteryx.com/us/en/search?q=${q}` },
  { match: "arcteryx",            search: (q) => `https://arcteryx.com/us/en/search?q=${q}` },
  { match: "the north face",      search: (q) => `https://www.thenorthface.com/en-us/search?q=${q}` },
  { match: "north face",          search: (q) => `https://www.thenorthface.com/en-us/search?q=${q}` },
  { match: "columbia",            search: (q) => `https://www.columbia.com/search?q=${q}` },
  { match: "athleta",             search: (q) => `https://athleta.gap.com/browse/search.do?searchText=${q}` },
  { match: "girlfriend collective", search: (q) => `https://girlfriend.com/search?q=${q}` },
  { match: "cotopaxi",            search: (q) => `https://www.cotopaxi.com/search?q=${q}` },
  { match: "vuori",               search: (q) => `https://vuoriclothing.com/search?q=${q}` },
  { match: "alo yoga",            search: (q) => `https://www.aloyoga.com/search?q=${q}` },
  { match: "outdoor voices",      search: (q) => `https://www.outdoorvoices.com/search?q=${q}` },
  { match: "tracksmith",          search: (q) => `https://www.tracksmith.com/search?q=${q}` },

  // ── Footwear ─────────────────────────────────────────────────────────────
  { match: "allbirds",            search: (q) => `https://www.allbirds.com/search?q=${q}` },
  { match: "vans",                search: (q) => `https://www.vans.com/en-us/search?q=${q}` },
  { match: "converse",            search: (q) => `https://www.converse.com/shop/search?q=${q}` },
  { match: "dr. martens",         search: (q) => `https://www.drmartens.com/us/en/search?q=${q}` },
  { match: "dr martens",          search: (q) => `https://www.drmartens.com/us/en/search?q=${q}` },
  { match: "birkenstock",         search: (q) => `https://www.birkenstock.com/us/search?q=${q}` },
];

// Sort longest-match-first so e.g. "amazon essentials" beats a bare "amazon"
// and "the north face" beats "north face".
const BRANDS_BY_LEN = [...BRANDS].sort((a, b) => b.match.length - a.match.length);

// Broad multi-brand Skimlinks merchant used when the named brand isn't in the
// explicit map. Searching the full "Brand Product" string here works because
// Nordstrom is multi-brand and carries a wide range.
const FALLBACK_SEARCH = (q) => `https://www.nordstrom.com/sr?keyword=${q}`;

// Strip the "(~$XX)" price suffix the Stylist prompt appends to each example.
function stripPrice(raw) {
  return String(raw).replace(/\s*\(\s*~?\$\s*[\d,]+\s*\)\s*$/, "").trim();
}

// Turn an example string ("Buck Mason Slub Henley (~$58)") into { label, url }.
// label is the original string (with price) for display; url is the search
// link. Known brands link to their own site (searching just the product
// portion); unknown brands link to a Nordstrom search for the full string so
// every example is still a monetizable affiliate link.
export function linkForExample(raw) {
  const label = String(raw);
  const text = stripPrice(raw);
  const lower = text.toLowerCase();
  const brand = BRANDS_BY_LEN.find((b) => lower.startsWith(b.match));
  if (brand) {
    // Search the product portion (drop the brand prefix) on the brand's own
    // site — "slub henley" on buckmason.com beats "buck mason slub henley",
    // which can return nothing on a single-brand store.
    const product = text.slice(brand.match.length).trim() || text;
    return { label, url: brand.search(encodeURIComponent(product)) };
  }
  // Unknown brand → full-string search on a broad multi-brand merchant.
  return { label, url: FALLBACK_SEARCH(encodeURIComponent(text)) };
}
